/**
 * 「咖位阶梯」弹窗（2026-10-02，只读）。入口＝顶栏那枚 .level-badge（决策页/人脉页/微博页三枚同绑，
 * app.ts 一条事件委托），点开看的是「我现在在哪、下一级要什么」。
 *
 * 样板 = CareerRecordPanel / HeatChartPanel：modal-wrap + modal-note-card 独立覆盖层，⛔ 进 app 的 panels 注册表。
 *
 * 展示口径（用户 2026-10-02 定，同日三改后定稿＝二改的数据口径 + 三改的保密范围）：
 *  - 头部只报两个读数：名气、口碑（口碑给现值，Lv.8+ 卡住人的就是它，⛔ 只报名气会让人无从自查）；
 *  - 十行阶梯 = 咖位名 / 晋升所需名气 / 口碑门槛（**⛔ 等级列**——咖位名本身就是阶梯，Lv. 号是工程口径）；
 *    当前等级整行高亮；
 *  - **两列同口径＝都写「爬到本级」的入门要求**（`thresholds[lv-1]` 与 `getReputationGate(lv)`）：
 *    ⚠️ 曾把名气列错位成「离开本级」（无人在意那行写 30、满级写 `—`），用户判「确实存在歧义」后**撤回**——同一行两列⛔ 一个讲进一个讲出；
 *  - **保密范围（10-02 五改后定稿）**：咖位名**只到当前级为止**，往上（含紧邻下一级）一律 `？？？`（三改保留：下一级名字⛔ 剧透）；
 *    但**下一级的两项门槛都公开**（名气＋口碑，无口碑门槛那格同样显 `—`）——玩家要的是"下一步要凑齐什么"，⛔ 把目标数字藏起来；
 *    再往上（下两级及以后）两格一律**留白**（⛔ 满屏问号＝像渲染坏了）；
 *  - Lv.1~7 本就没有口碑门槛，已达那几行该格为 `—`（读 rankSystem.getReputationGate() 的 null，⛔ 前端编数）；
 *    Lv.1 同理是出身档⛔ 名气门槛 ⇒ 「晋升所需名气」那格也走同一条 `—`（旧文案「起步」10-02 用户令退役）；
 *  - 当前行带一条到下一级的进度条＋差额数字（读现成 getRankProgress / getFameToNextRank，满级显示「已至顶」）。
 *
 * ⛔ 落盘任何字段（名气/口碑都是活账本）⇒ 每次打开重算，⛔ 缓存。
 */
import { GameState } from '../../systems/GameState';
import { GameConfig } from '../../core/GameConfig';
import { rankSystem } from '../../systems/RankSystem';
import { createDiv, createModalDismiss, createSpan } from '../ui';

export class RankLadderPanel {
    readonly element: HTMLElement;
    private readonly headEl: HTMLElement;
    private readonly body: HTMLElement;
    /** 开窗关窗通知宿主（悬浮入口要让位，与实绩/榜单弹窗同一条规矩） */
    onOpenChanged: ((open: boolean) => void) | null = null;

    constructor() {
        const wrap = createDiv('modal-wrap rankladder-pop');
        const card = createDiv('modal-note-card rankladder-card');
        card.appendChild(createDiv('mtitle', ['咖位阶梯']));
        this.headEl = createDiv('rankladder-head');
        card.appendChild(this.headEl);
        this.body = createDiv('rankladder-body');
        card.appendChild(this.body);
        // 2026-10-16 弹窗统一批（茄子令）：卡内那颗「关闭」删掉，关闭热区＝遮罩本体（见 ui.ts createModalDismiss）
        wrap.appendChild(createModalDismiss(() => this.close()));
        wrap.appendChild(card);

        this.element = wrap;
    }

    get isOpen(): boolean {
        return this.element.classList.contains('show');
    }

    open(): void {
        if (this.isOpen) return;
        this.render();
        this.element.classList.add('show');
        this.onOpenChanged?.(true);
    }

    close(): void {
        if (!this.isOpen) return;
        this.element.classList.remove('show');
        this.onOpenChanged?.(false);
    }

    private render(): void {
        const state = GameState.getInstance();
        const rank = state.player.rank;
        const fame = state.getFame();
        const rep = state.getAttribute('reputation');
        const names = rankSystem.getRankNames();
        const thresholds = rankSystem.getThresholds();
        const maxRank = GameConfig.RANK_MAX;

        // ── 头部：两个读数 + 到下一级的进度
        this.headEl.innerHTML = '';
        const nums = createDiv('rankladder-nums');
        nums.appendChild(createSpan('rl-num', `名气 ${fame}`));
        nums.appendChild(createSpan('rl-num', `口碑 ${rep}`));
        this.headEl.appendChild(nums);

        const atMax = rank >= maxRank;
        const nextGate = rankSystem.getReputationGate(rank + 1);
        const nextWorkGate = rankSystem.getWorkGate(rank + 1);
        const nextAwardGate = rankSystem.getAwardGate(rank + 1);
        const bar = createDiv('rl-bar');
        const fill = createDiv('rl-bar-fill');
        fill.style.width = atMax ? '100%' : `${Math.round(rankSystem.getRankProgress(rank, fame) * 100)}%`;
        bar.appendChild(fill);
        this.headEl.appendChild(bar);
        // 下一级的「另需」：口碑 ＋ 作品 ＋ 奖项（2026-10-22 咖位曲线批：顶流后名气只是入场券）
        const extras: string[] = [];
        if (nextGate !== null) extras.push(`口碑 ${nextGate}`);
        if (nextWorkGate !== null) extras.push(`作品 ${nextWorkGate} 部`);
        if (nextAwardGate !== null) extras.push(nextAwardGate === 2 ? '双料影后' : '女主角奖 1 座');
        // 缺口句按「缺什么」说，⛔ 不只报名气（2026-10-23 UI视觉 提出的误导点）：
        // 名气已够但口碑/作品门卡住时，旧句会读「还差 0 名气晋升下一咖位」——数字没错、意思全错。
        const fameGap = rankSystem.getFameToNextRank(rank);
        const famePart = fameGap > 0 ? `还差 ${fameGap} 名气` : '名气已达标';
        const hint = createSpan('rl-hint', atMax
            ? `已至顶 · ${names[maxRank - 1]}`
            : `${famePart}晋升下一咖位`
            + (extras.length > 0 ? `（该级另需 ${extras.join(' · ')}）` : ''));
        this.headEl.appendChild(hint);

        // ── 十行阶梯（⛔ 等级列；咖位名只到当前级为止，往后的连名字一起保密）
        this.body.innerHTML = '';
        const cols = createDiv('rl-row rl-cols');
        cols.appendChild(createSpan('rl-name', '咖位'));
        cols.appendChild(createSpan('rl-fame', '晋升所需名气'));
        cols.appendChild(createSpan('rl-rep', '口碑门槛'));
        cols.appendChild(createSpan('rl-work', '作品/奖项'));
        this.body.appendChild(cols);
        for (let lv = 1; lv <= maxRank; lv++) {
            const row = createDiv('rl-row');
            const reached = lv <= rank;                       // 走过 ⇒ 咖位名与两项门槛可见
            const isNext = lv === rank + 1;                   // 下一级 ⇒ 只露「晋升所需名气」，名字照样保密
            const gate = rankSystem.getReputationGate(lv);
            const workGate = rankSystem.getWorkGate(lv);
            const awardGate = rankSystem.getAwardGate(lv);
            const extra: string[] = [];
            if (workGate !== null) extra.push(`作品${workGate}`);
            if (awardGate !== null) extra.push(awardGate === 2 ? '双料' : '奖1');
            const extraText = extra.length > 0 ? extra.join('·') : '—';

            // 「晋升所需名气」＝**爬到本级**要的名气（与口碑门槛同口径：都是这一级的入门要求）
            // Lv.1 是出身档⛔ 门槛 ⇒ 与「无口碑要求」共用同一条 `—`（旧文案「起步」已退役，10-02 用户令）
            row.appendChild(createSpan('rl-name', reached ? names[lv - 1] : '？？？'));
            row.appendChild(createSpan('rl-fame',
                lv === 1 ? '—' : reached || isNext ? String(thresholds[lv - 1]) : ''));
            row.appendChild(createSpan('rl-rep',
                reached || isNext ? (gate === null ? '—' : String(gate)) : ''));
            row.appendChild(createSpan('rl-work',
                reached || isNext ? extraText : ''));

            if (lv === rank) row.classList.add('is-current');
            else if (lv < rank) row.classList.add('is-past');
            else row.classList.add('is-future');
            this.body.appendChild(row);
        }
    }
}
